"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth-context";
import VerifyEmailGate from "@/components/VerifyEmailGate";

// The interactive Mapbox app (was this route's whole page). Deferred so
// its bundle never loads for anonymous visitors / crawlers - they get
// the server-rendered SEO landing instead.
const MapInteractive = dynamic(() => import("./MapInteractive"), {
  ssr: false,
  loading: () => <div className="fixed inset-0 bg-gray-50 animate-pulse" />,
});

/**
 * Signed-in users get the live interactive map; everyone else - including
 * Googlebot - gets the server-rendered SEO landing passed as `children`.
 * The landing is what renders in the SSR HTML (user is unknown until the
 * client auth check resolves), so crawlers always index the content.
 */
export default function MapExperience({ children, bot = false }: { children: ReactNode; bot?: boolean }) {
  const { user, loading } = useAuth();
  // A crawler never has a session: the landing, with no loading shell.
  if (bot) return <>{children}</>;
  // While the session cookie is still being checked, paint a neutral
  // surface instead of the signed-out landing: a signed-in visitor used
  // to see the marketing version flash for a second on every reload.
  if (loading) return <div className="fixed inset-0 bg-gray-50 animate-pulse" aria-busy="true" />;
  if (!user) return <>{children}</>;
  // Signed in but email not confirmed: block the feature with a verify
  // prompt (the /api/heatmap routes return 403 email_not_verified anyway).
  if (user.email_verified === false) return <VerifyEmailGate />;
  return <MapInteractive />;
}
